import React from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MerchantStep } from "../types/merchantRegister.types";

interface MerchantRegisterStepperProps {
  steps: MerchantStep[];
  currentStep: number;
}

/**
 * Displays the five visible registration stages using the auth design tokens.
 * Completed stages use the brand teal, while the current stage is emphasized
 * with a ring and pending stages remain visually quiet.
 */
export const MerchantRegisterStepper: React.FC<MerchantRegisterStepperProps> = ({
  steps,
  currentStep,
}) => {
  const { t } = useTranslation();

  if (currentStep === 5) return null;

  const visibleSteps = steps.slice(0, 5);

  return (
    <nav
      aria-label={t("merchantRegister.progress.label")}
      className="w-full px-1 py-1"
    >
      <div className="mb-4 text-center sm:hidden">
        <p className="text-xs font-semibold uppercase text-[#7B8794] dark:text-gray-400">
          {t("merchantRegister.progress.count", {
            current: currentStep + 1,
            total: visibleSteps.length,
          })}
        </p>
        <p className="mt-1 text-sm font-bold text-[#004232] dark:text-[#00C49A]">
          {visibleSteps[currentStep]?.title}
        </p>
      </div>
      <ol
        className="grid grid-cols-5 gap-0"
        aria-hidden="true"
      >
        {visibleSteps.map((step, index) => {
          const isComplete = index < currentStep;
          const isCurrent = index === currentStep;

          return (
            <li
              key={step.id}
              aria-current={isCurrent ? "step" : undefined}
              className="relative flex min-w-0 flex-col items-center"
            >
              {index < visibleSteps.length - 1 && (
                <span
                  aria-hidden="true"
                  className={`absolute left-1/2 top-4 h-px w-full transition-colors ${
                    isComplete ? "bg-[#00C49A]" : "bg-[#D7DEE5] dark:bg-gray-700"
                  }`}
                />
              )}

              <span
                className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold transition-all ${
                  isComplete
                    ? "border-[#00C49A] bg-[#00C49A] text-[#004232]"
                    : isCurrent
                      ? "border-[#00C49A] bg-white text-[#004232] ring-4 ring-[#00C49A]/15 dark:bg-gray-900 dark:text-[#00C49A]"
                      : "border-[#B8C1CB] bg-white text-[#7B8794] dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400"
                }`}
              >
                {isComplete ? (
                  <Check
                    size={16}
                    strokeWidth={3}
                  />
                ) : (
                  index + 1
                )}
              </span>

              <span
                className={`mt-3 hidden px-1 text-center text-[11px] font-semibold leading-4 transition-colors sm:block sm:text-xs ${
                  isCurrent || isComplete
                    ? "text-[#004232] dark:text-[#00C49A]"
                    : "text-[#7B8794] dark:text-gray-400"
                }`}
              >
                {step.title}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
